/**
 * GwMonitoringHealth — compact "Monitoring" pill + drill-down for the
 * GW Connection Card.
 *
 * Reads /api/ops/monitoring/google_workspace and renders:
 *   • A single health pill (Healthy / Stalled / Failing / Never)
 *   • Last successful collection (relative + tooltip absolute)
 *   • Last successful evaluation
 *   • Next scheduled collect + evaluate times
 *   • Dead jobs count w/ a link to the failing-jobs page
 *
 * Self-refreshes every 60s while mounted; can also be triggered manually.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, Clock, AlertTriangle, CheckCircle2, MinusCircle, RefreshCw, Power,
} from 'lucide-react';
import { Switch } from './ui/switch';
import { parseServerDate } from '../utils/dateFormatter';

const API = process.env.REACT_APP_BACKEND_URL || '';

const HEALTH_META = {
  healthy: {
    label: 'Monitoring healthy',
    tone: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Icon: CheckCircle2,
  },
  stalled: {
    label: 'Monitoring stalled',
    tone: 'bg-amber-50 text-amber-800 border-amber-200',
    Icon: AlertTriangle,
  },
  failing: {
    label: 'Monitoring failing',
    tone: 'bg-red-50 text-red-700 border-red-200',
    Icon: AlertTriangle,
  },
  never: {
    label: 'Not yet running',
    tone: 'bg-slate-50 text-slate-600 border-slate-200',
    Icon: MinusCircle,
  },
};

const fmtRelative = (iso) => {
  const d = parseServerDate(iso);
  if (!d) return 'never';
  const t = d.getTime();
  const diff = Date.now() - t;
  if (diff < 0) {
    // future
    const m = Math.ceil(-diff / 60000);
    if (m < 60) return `in ${m}m`;
    const h = Math.ceil(m / 60);
    if (h < 24) return `in ${h}h`;
    return `in ${Math.ceil(h / 24)}d`;
  }
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
};

const fmtAbsolute = (iso) => {
  const d = parseServerDate(iso);
  if (!d) return '—';
  return d.toLocaleString();
};

export const GwMonitoringHealth = ({ source = 'google_workspace' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/ops/monitoring/${source}`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not fetch monitoring health');
    } finally {
      setLoading(false);
    }
  }, [source]);

  const toggleAuto = useCallback(async (next) => {
    setToggling(true);
    try {
      await axios.post(`${API}/api/ops/monitoring/${source}/auto-collection`, { enabled: next });
      toast.success(next
        ? 'Automatic collection resumed'
        : 'Automatic collection paused — evidence will only be fetched when you click Collect Evidence');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update automatic collection');
    } finally {
      setToggling(false);
    }
  }, [source, load]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);  // refresh every 60s
    return () => clearInterval(id);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="gw-monitoring-loading">
        <RefreshCw className="w-3 h-3 animate-spin" /> Loading monitoring…
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center gap-2 text-xs text-red-600" data-testid="gw-monitoring-error">
        <AlertTriangle className="w-3 h-3" /> {error}
      </div>
    );
  }
  if (!data) return null;

  const meta = HEALTH_META[data.health] || HEALTH_META.never;
  const Icon = meta.Icon;
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-3 space-y-2"
      data-testid="gw-monitoring-card"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium rounded-md border ${meta.tone}`}
            data-testid={`gw-monitoring-pill-${data.health}`}
          >
            <Icon className="w-3 h-3" />
            {meta.label}
          </span>
          <span className="text-[11px] text-slate-500 flex items-center gap-1">
            <Activity className="w-3 h-3" />
            {data.active_schedules || 0} schedule{data.active_schedules === 1 ? '' : 's'} active
          </span>
        </div>
        <button
          type="button"
          onClick={load}
          className="text-[11px] text-slate-400 hover:text-slate-600 inline-flex items-center gap-1"
          data-testid="gw-monitoring-refresh-btn"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
      </div>

      {data.has_schedules && (
        <div
          className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5"
          data-testid="gw-monitoring-auto-toggle-row"
        >
          <div className="flex items-center gap-1.5 min-w-0">
            <Power className={`w-3.5 h-3.5 ${data.auto_collection_enabled ? 'text-emerald-600' : 'text-slate-400'}`} />
            <span className="text-[11px] font-medium text-slate-700">Automatic collection</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${data.auto_collection_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
              {data.auto_collection_enabled ? 'On' : 'Off'}
            </span>
          </div>
          <Switch
            checked={!!data.auto_collection_enabled}
            disabled={toggling}
            onCheckedChange={(v) => toggleAuto(v)}
            data-testid="gw-monitoring-auto-toggle"
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-700">
        <div title={fmtAbsolute(data.last_collected_at)}>
          <p className="text-slate-500 uppercase tracking-wide text-[10px]">Last collection</p>
          <p
            className={data.collect_freshness?.badge === 'stale' ? 'text-amber-700 font-medium' : 'text-slate-800'}
            data-testid="gw-monitoring-last-collected"
          >
            {fmtRelative(data.last_collected_at)}
          </p>
        </div>
        <div title={fmtAbsolute(data.last_evaluated_at)}>
          <p className="text-slate-500 uppercase tracking-wide text-[10px]">Last evaluation</p>
          <p
            className={data.evaluate_freshness?.badge === 'stale' ? 'text-amber-700 font-medium' : 'text-slate-800'}
            data-testid="gw-monitoring-last-evaluated"
          >
            {fmtRelative(data.last_evaluated_at)}
          </p>
        </div>
        <div title={fmtAbsolute(data.next_collect_at)}>
          <p className="text-slate-500 uppercase tracking-wide text-[10px]">Next collect</p>
          <p className="text-slate-700 inline-flex items-center gap-1" data-testid="gw-monitoring-next-collect">
            <Clock className="w-3 h-3" />
            {fmtRelative(data.next_collect_at)}
          </p>
        </div>
        <div title={fmtAbsolute(data.next_evaluate_at)}>
          <p className="text-slate-500 uppercase tracking-wide text-[10px]">Next evaluate</p>
          <p className="text-slate-700 inline-flex items-center gap-1" data-testid="gw-monitoring-next-evaluate">
            <Clock className="w-3 h-3" />
            {fmtRelative(data.next_evaluate_at)}
          </p>
        </div>
      </div>

      {data.dead_jobs_count > 0 && (
        <div className="border-t border-red-100 pt-2">
          <p className="text-[11px] text-red-700 font-medium flex items-center gap-1" data-testid="gw-monitoring-dead-jobs">
            <AlertTriangle className="w-3 h-3" />
            {data.dead_jobs_count} dead job{data.dead_jobs_count === 1 ? '' : 's'} need attention
          </p>
          {(data.recent_dead_jobs || []).slice(0, 2).map((j) => (
            <p key={j.id} className="text-[10px] text-red-600 mt-1 truncate" title={j.error || ''}>
              · <code>{j.kind}</code> after {j.attempts} attempts — {(j.error || 'unknown error').slice(0, 80)}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default GwMonitoringHealth;
